export interface IVcfUserCapabilitiesResponse {
    data: {
        id: string;
        status: number[];
        expirationDate: string;
        userDisablingAllowed: boolean;
    }[];
}

export interface INotification {
    category: 'speedalert' | 'geofence' | 'antitheftalarm' | 'honkflash' | 'remotelockunlock' | 'digitalkey';
    action: string;
    timestamp: string;
}

export interface INotificationSpeedAlert extends INotification {
    category: 'speedalert';
    allowedSpeedLimit_kmph: number;
    alertName: string;
}

export interface INotificationGeofence extends INotification {
    category: 'geofence';
    alertName: string;
    action: 'enter_redzone' | 'exit_greenzone';
}

export interface INotificationAntiTheftAlarm extends INotification {
    category: 'antitheftalarm';
}

export interface INotificationHonkFlash extends INotification {
    category: 'honkflash';
    actionSuccess: boolean;
}

export interface INotificationRemoteLockUnlock extends INotification {
    category: 'remotelockunlock';
    actionSuccess: boolean;
}

export interface INotificationDigitalKey extends INotification {
    category: 'digitalkey';
    action:
        | 'mk_extended'
        | 'mk_installed'
        | 'mk_removal_failed_verwalter'
        | 'mk_removal_succeeded_verwalter'
        | 'mk_returned'
        | 'permission_and_mk_expired'
        | 'permission_and_mk_revoked_verwalter'
        | 'permission_declined'
        | 'permission_expired'
        | 'permission_granted'
        | 'permission_returned'
        | 'permission_revoked_verwalter'
        | 'permission_vtan_rejected';
}

export interface IVcfTransactionHistoryGeofenceResponse {
    readonly data?: INotification[];
}

export interface TransactionHistoryMessage {
    category: string;
    action: string;
    timestamp: string;
}

export interface IVcfTransactionHistoryAccess extends TransactionHistoryMessage {
    actionSuccess: boolean;
}

export interface IVcfTransactionHistoryGeofence extends TransactionHistoryMessage {
    alertName: string;
}

export interface IVcfTransactionHistorySpeedAlert extends TransactionHistoryMessage {
    alertName: string;
    allowedSpeedLimit_kmph: number;
}

export interface TransactionHistoryResponse {
    data: {
        messages: TransactionHistoryMessage[];
        errors: { category: TransactionHistoryCategory; error: { code: number; message: string } }[];
    };
}

export type VcfGeofenceZone = 'exit' | 'enter';

export type VcfGeofenceScheduleType = 'recurring' | 'single';

export interface INotificationSchedule {
    type: 'recurring' | 'single';
    startDateTime?: string | null;
    endDateTime?: string | null;
    recurringOn?: {
        mondays: boolean;
        tuesdays: boolean;
        wednesdays: boolean;
        thursdays: boolean;
        fridays: boolean;
        saturdays: boolean;
        sundays: boolean;
    } | null;
    startTime?: string | null;
    endTime?: string | null;
}

export interface IVcfGeofence {
    enabled: boolean;
    id: number;
    alertName: string;
    zone: 'exit' | 'enter';
    area: {
        type: 'rectangle' | 'circle';
        lat: number;
        lon: number;
        width: number | null;
        height: number | null;
        radius: number;
    };
    schedule: {
        type: 'recurring' | 'single';
        startDateTime: string | null;
        endDateTime: string | null;
        recurringOn: {
            mondays: boolean;
            tuesdays: boolean;
            wednesdays: boolean;
            thursdays: boolean;
            fridays: boolean;
            saturdays: boolean;
            sundays: boolean;
        } | null;
        startTime: string | null;
        endTime: string | null;
    } | null;
}

export interface IVcfSpeedAlert {
    enabled: boolean;
    id: number;
    alertName: string;
    allowedSpeedLimit_kmph: number;
    schedule: INotificationSchedule | null;
}

export interface IVcfGeofenceResponse {
    data: {
        carCapturedTimestamp: string;
        maxDefinitions: number;
        maxActiveDefinitions: number;
        geofences: IVcfGeofence[];
    };
}

export interface IVcfGeofenceCreatePayload {
    callback: string;
    geofence: {
        enabled: boolean;
        alertName: string;
        zone: string;
        area: any;
        schedule: INotificationSchedule | null;
    };
}

export interface IVcfSpeedAlertCreatePayload {
    callback: string;
    speedAlert: {
        enabled: boolean;
        alertName: string;
        allowedSpeedLimit_kmph: number;
        schedule: INotificationSchedule | null;
    };
}

export interface IVcfGeofenceUpdatePayload {
    callback: string;
    geofences: [
        {
            id: number;
            enabled: boolean;
            alertName: string;
            zone: string;
            area: any;
            schedule: any;
        },
    ];
}

export interface IVcfNotificationCreateOrUpdateResponse {
    data: {
        requestID: string;
    };
}

export interface IVcfSpeedAlertResponse {
    data: {
        carCapturedTimestamp: string;
        maxDefinitions: number;
        maxActiveDefinitions: number;
        speedAlerts: IVcfSpeedAlert[];
    };
}

export interface IVcfSpeedAlertUpdatePayload {
    callback: string;
    speedAlerts: IVcfSpeedAlert[];
}

export type Path = undefined | 'last' | 'speedalert' | 'geofence' | 'antitheftalarm' | 'honkflash' | 'access';

export type VcfNotificationSchedule = {
    type: 'recurring' | 'single';
    startDateTime: string | null;
    endDateTime: string | null;
    recurringOn: {
        mondays: boolean;
        tuesdays: boolean;
        wednesdays: boolean;
        thursdays: boolean;
        fridays: boolean;
        saturdays: boolean;
        sundays: boolean;
    } | null;
    startTime: string | null;
    endTime: string | null;
} | null;

export interface IVcfNotificationSchedule {
    type: 'single' | 'recurring';
    startDateTime: string | null;
    endDateTime: string | null;
    recurringOn: {
        mondays: false;
        tuesdays: false;
        wednesdays: false;
        thursdays: false;
        fridays: false;
        saturdays: false;
        sundays: false;
    } | null;
    startTime: string | null;
    endTime: string | null;
}

export interface ISubscriptionSettingsTopic {
    id: 'theftalarm' | 'vehicleHealth';
    eMail: boolean;
}
export interface ISubscriptionSettingsResponse {
    data: {
        topics: ISubscriptionSettingsTopic[];
    };
}

export enum Category {
    ALL = 'all',
    SPEEDALERT = 'speedalert',
    GEOFENCE = 'geofence',
    ANTITHEFTALARM = 'antitheftalarm',
    TRANSACTION_HISTORY_ANTITHEFT_ALERT = 'transactionHistoryAntiTheftAlert',
    HONKFLASH = 'honkflash',
    REMOTELOCKUNLOCK = 'remotelockunlock',
}

export type TransactionHistoryCategory =
    | 'speedalert'
    | 'geofence'
    | 'antitheftalarm'
    | 'honkflash'
    | 'access'
    | 'remotelockunlock';

export enum TransactionHistoryErrorStatus {
    'UNKNOWN',
    'NO_CAPABILITIES',
}

export interface TransactionHistoryResponseData {
    messages?: [];
    errors?: {
        category: Category;
        error: {
            code: number;
            message: string;
        };
    }[];
    errorStatus?: TransactionHistoryErrorStatus;
    isEditTabVisible?: boolean;
}

export interface IVcfNotificationDeleteResponse {
    data: {
        requestID: string;
    };
}

export type NotificationConfigStatus = 'in_progress' | 'delayed' | 'error' | 'successful';
export interface NotificationConfigRequestStatusResponse {
    data: {
        status: NotificationConfigStatus;
    };
}
